import { ModelNamespaces, PermissionModel } from "@granular/authz";
import { GKIllustration, GKIllustrationName } from "@granular/gds";
import { PropsWithChildren } from "react";
import { FormattedMessage, IntlProvider, useIntl } from "react-intl";
import { DEFAULT_LOCALE } from "@granular/fabric3-definitions";
import translations from "../../i18n";
import { useUserLocale } from "../hooks/locale/useUserLocale";
import { useOperatingContext } from "../hooks/operatingContext";

type PermissionGuardProps = PropsWithChildren<{
  /**
   * The permission model required to access the content. Accepts a resolved
   * model value (e.g. ModelNamespaces.Fabric3.app_proposals) or a key string
   * (e.g. "app_proposals" from package.json metadata). When undefined, children
   * are rendered unconditionally.
   */
  model: string | undefined;
  /**
   * When true, renders a compact inline version suitable for embedding inside
   * a page. When false (default), renders as a full-page message.
   */
  compact?: boolean;
}>;

/**
 * This component only mounts its children if the user has permission to view
 * the given model. Use this to gate app content that requires a specific role.
 * When no model is provided, children are always rendered.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  model,
  compact = false,
}) => {
  const { userCan } = useOperatingContext();

  if (!model) {
    return children;
  }

  const resolved =
    model in ModelNamespaces.Fabric3
      ? ModelNamespaces.Fabric3[model as keyof typeof ModelNamespaces.Fabric3]
      : (model as PermissionModel);

  if (userCan("view", resolved)) {
    return children;
  }

  return <PermissionMessage compact={compact} />;
};

export const PermissionMessage: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const locale = useUserLocale();
  return (
    <IntlProvider
      locale={locale}
      messages={translations[locale]}
      defaultLocale={DEFAULT_LOCALE}
    >
      <PermissionMessageContent compact={compact} />
    </IntlProvider>
  );
};

const PermissionMessageContent: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const intl = useIntl();
  const helpLink = intl.formatMessage({ id: "no_context_guard_help_link" });

  return (
    <div
      data-qa="permission-guard"
      style={{
        width: "100%",
        textAlign: "center",
        padding: compact ? "1.5rem 1rem" : "7rem 1rem",
      }}
    >
      <GKIllustration
        name={GKIllustrationName.Key}
        style={{
          display: "inline",
          width: compact ? "64px" : "96px",
          height: compact ? "64px" : "96px",
          margin: compact ? "1rem" : "2rem",
        }}
      />
      <h4>
        <FormattedMessage id="access_denied_guard_header" />
      </h4>
      <p>
        <FormattedMessage id="access_denied_guard_text" />
      </p>
      <p className="text-muted">
        <FormattedMessage
          id="no_context_guard_help_link_text"
          values={{
            // This substitutes the `<link>` placeholders into actual `<a>` tags.
            link: (chunks) => <a href={helpLink}>{chunks}</a>,
          }}
        />
      </p>
    </div>
  );
};

export default PermissionGuard;
